import { useState, useEffect, useRef } from 'react';
import './BizMediabankVideo.scss';
import { MediabankFile } from '../../Types/mediabank';
import { getPathWithRootPath } from '../../Services/common';
import { getOneFile } from '../../Services/mediabank';
import { logUserStatus, logUserStatusOnUnload, eventStart, eventEnd, EngageStatus } from '../../Services/engageTracking';
import { ObjectType } from '../../Types/base';

type YoutubePlayerInstance = {
	playVideo: () => void;
};

type YoutubePlayerConstructor = new (
	elementId: string | HTMLElement,
	options: {
		videoId: string;
		playerVars: { rel: number };
		width: string;
		events: {
			onReady: () => void;
			onStateChange: (event: { data: number }) => void;
		};
	}
) => YoutubePlayerInstance;

type VimeoPlayerInstance = {
	on: (eventName: string, callback: (data?: unknown) => void) => void;
	play: () => void;
};

type BrightcovePlayerInstance = {
	on: (eventName: string, callback: () => void) => void;
};

declare global {
	interface Window {
		onYouTubeIframeAPIReady?: () => void;
		YT?: {
			Player: YoutubePlayerConstructor;
		};
		Vimeo?: {
			Player: new (
				container: HTMLElement,
				options: {
					id: string;
					responsive: boolean;
					autoplay: boolean;
				}
			) => VimeoPlayerInstance;
		};
		bc?: (elementId: string) => BrightcovePlayerInstance;
	}
}

type VideoType = 'Native' | 'Youtube' | 'Vimeo' | 'Brightcove' | 'Mediaflow' | 'External';

const VIDEO_EVENTS = {
	Start: 12,
	End: 13,
	Init: 14,
};

const VIDEO_TYPE_MATCHERS: { type: VideoType; regex: RegExp; videoCodeIndex?: number }[] = [
	{
		type: 'Youtube',
		regex: /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/i,
		videoCodeIndex: 1,
	},
	{ type: 'Vimeo', regex: /vimeo\.com\//i },
	{ type: 'Brightcove', regex: /players\.brightcove\.net/i },
	{ type: 'Mediaflow', regex: /mediaflow\.com/i },
	{ type: 'External', regex: /^https?:\/\//i },
];

let videoIndex = 0;

type BizMediabankVideoProps = {
	file?: MediabankFile;
	fileId?: number;
	linkedObjectType?: ObjectType;
	linkedObjectId?: number;
	autoplay?: boolean;
	showPoster?: boolean;
	showInPopup?: boolean;
	onStarted?: (file: MediabankFile) => void;
	onPlayed?: (file: MediabankFile) => void;
};

const BizMediabankVideo = ({
	file: fileProp,
	fileId,
	linkedObjectType,
	linkedObjectId,
	autoplay = false,
	showPoster: showPosterProp = true,
	showInPopup = false,
	onStarted,
	onPlayed,
}: BizMediabankVideoProps) => {
	const [file, setFile] = useState<MediabankFile | undefined>(fileProp);
	const [showPopupPlayer, setShowPopupPlayer] = useState(false);

	useEffect(() => {
		if (fileProp) setFile(fileProp);
	}, [fileProp]);

	useEffect(() => {
		if (!fileProp && fileId && fileId > 0) {
			getOneFile(fileId).then((result) => {
				if (result) setFile(result);
			});
		}
	}, [fileId, fileProp]);

	const getThumbnailUrl = () => {
		if (!file?.Thumbnail) return '';
		return getPathWithRootPath(file.Thumbnail);
	};

	if (!file && !fileId) return null;

	const usePopup = showInPopup && Boolean(file?.Thumbnail?.trim());
	if (usePopup && !showPopupPlayer) {
		return (
			<div
				className='biz-mediabank-video-v2 biz-mediabank-video-poster'
				style={{ backgroundImage: `url(${getThumbnailUrl()})` }}
				onClick={() => setShowPopupPlayer(true)}
			>
				<div className='biz-mediabank-video-play-overlay' />
			</div>
		);
	}

	if (usePopup && showPopupPlayer) {
		return (
			<div className='biz-mediabank-video-v2-popup-overlay' onClick={() => setShowPopupPlayer(false)}>
				<div className='biz-mediabank-video-v2-popup' onClick={(e) => e.stopPropagation()}>
					<BizMediabankVideoPlayer
						file={file}
						fileId={fileId}
						linkedObjectType={linkedObjectType}
						linkedObjectId={linkedObjectId}
						autoplay={true}
						showPoster={false}
						onStarted={onStarted}
						onPlayed={onPlayed}
					/>
				</div>
			</div>
		);
	}

	return (
		<BizMediabankVideoPlayer
			file={file}
			fileId={fileId}
			linkedObjectType={linkedObjectType}
			linkedObjectId={linkedObjectId}
			autoplay={autoplay}
			showPoster={showPosterProp}
			onStarted={onStarted}
			onPlayed={onPlayed}
		/>
	);
};

// --- Inner player component ---

type BizMediabankVideoPlayerProps = {
	file?: MediabankFile;
	fileId?: number;
	linkedObjectType?: ObjectType;
	linkedObjectId?: number;
	autoplay?: boolean;
	showPoster?: boolean;
	onStarted?: (file: MediabankFile) => void;
	onPlayed?: (file: MediabankFile) => void;
};

const BizMediabankVideoPlayer = ({
	file: fileProp,
	fileId,
	linkedObjectType,
	linkedObjectId,
	autoplay = false,
	showPoster: showPosterProp = true,
	onStarted,
	onPlayed,
}: BizMediabankVideoPlayerProps) => {
	const [file, setFile] = useState<MediabankFile | undefined>(fileProp);
	const [showPoster, setShowPoster] = useState(showPosterProp);
	const [initialized, setInitialized] = useState(false);
	const [videoType, setVideoType] = useState<VideoType>('Native');
	const [videoCode, setVideoCode] = useState<string>('');

	const startedRef = useRef(false);
	const finishedRef = useRef(false);
	const videoElRef = useRef<HTMLVideoElement>(null);
	const youtubeContainerRef = useRef<HTMLDivElement>(null);
	const vimeoContainerRef = useRef<HTMLDivElement>(null);
	const brightcoveContainerRef = useRef<HTMLDivElement>(null);
	const mediaflowFrameRef = useRef<HTMLIFrameElement>(null);
	const playerRef = useRef<{ playVideo: () => void } | null>(null);
	const pendingPlayRef = useRef(false);
	const youtubePlaying = useRef(false);
	const videoIdRef = useRef(`bizmediabankvideo${videoIndex++}`);
	const mediaflowCleanupRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (fileProp) setFile(fileProp);
	}, [fileProp]);

	useEffect(() => {
		if (!fileProp && fileId && fileId > 0) {
			getOneFile(fileId).then((result) => {
				if (result) setFile(result);
			});
		}
	}, [fileId, fileProp]);

	useEffect(() => {
		if (file) initFile();
	}, [file]);

	useEffect(() => {
		const handleUnload = () => {
			if (file && startedRef.current && !finishedRef.current) {
				logUserStatusOnUnload(file.Id, ObjectType.MediabankFile, EngageStatus.Cancelled);
			}
		};
		window.addEventListener('beforeunload', handleUnload);
		return () => {
			handleUnload();
			window.removeEventListener('beforeunload', handleUnload);
			mediaflowCleanupRef.current?.();
		};
	}, [file]);

	const detectVideoType = (fileUrl: string): { type: VideoType; code?: string } => {
		for (const matcher of VIDEO_TYPE_MATCHERS) {
			const match = matcher.regex.exec(fileUrl);
			if (match) {
				const code = matcher.videoCodeIndex !== undefined ? match[matcher.videoCodeIndex] : undefined;
				return { type: matcher.type, code };
			}
		}
		return { type: 'Native' };
	};

	const getEventObjectId = () => {
		if (file && file.Id > 0) return file.Id;
		if (fileId && fileId > 0) return fileId;
		if (linkedObjectType && linkedObjectId && linkedObjectId > 0) return linkedObjectId;
		return 0;
	};

	const videoInitialized = () => {
		if (!linkedObjectType || !file) return;
		eventStart(getEventObjectId(), VIDEO_EVENTS.Init, linkedObjectType);
	};

	const videoStarted = () => {
		if (!file || startedRef.current) return;
		onStarted?.(file);
		startedRef.current = true;
		finishedRef.current = false;
		logUserStatus(file.Id, ObjectType.MediabankFile, EngageStatus.Started);
		if (!linkedObjectType) return;
		eventStart(getEventObjectId(), VIDEO_EVENTS.Start, linkedObjectType);
	};

	const videoEnded = () => {
		if (!file || !startedRef.current || finishedRef.current) return;
		onPlayed?.(file);
		finishedRef.current = true;
		startedRef.current = false;
		logUserStatus(file.Id, ObjectType.MediabankFile, EngageStatus.Finished, true);
		if (!linkedObjectType) return;
		eventEnd(getEventObjectId(), VIDEO_EVENTS.End, linkedObjectType);
	};

	const initFile = () => {
		if (!file) return;
		const detected = detectVideoType(file.FileUrl);
		setVideoType(detected.type);
		if (detected.code) setVideoCode(detected.code);

		// Only defer mounting when there is an actual poster to click.
		if (autoplay || !showPosterProp || !file.Thumbnail?.trim()) {
			setInitialized(true);
			initPlayerForType(detected.type, detected.code);
		}
	};

	const initPlayerForType = (type: VideoType, code?: string) => {
		switch (type) {
			case 'Native':
				videoInitialized();
				break;
			case 'Youtube':
				initYoutube(code ?? '');
				break;
			case 'Vimeo':
				initVimeo();
				break;
			case 'Brightcove':
				initBrightcove();
				break;
			case 'Mediaflow':
				initMediaflowPlayer();
				break;
			case 'External':
				videoInitialized();
				break;
		}
	};

	const handlePosterClick = () => {
		setShowPoster(false);
		setInitialized(true);
		pendingPlayRef.current = true;
		initPlayerForType(videoType, videoCode);
		setTimeout(() => {
			if (playerRef.current) playerRef.current.playVideo();
			else if (videoElRef.current) videoElRef.current.play();
		}, 200);
	};

	const initYoutube = (code: string) => {
		if (!code) return;
		setTimeout(() => {
			const container = youtubeContainerRef.current;
			const playerDocument = container?.ownerDocument;
			const playerWindow = playerDocument?.defaultView;
			if (!container || !playerWindow || !playerDocument) return;
			const youtube = playerWindow.YT;
			if (!youtube?.Player && playerWindow !== window) {
				const previousReady = playerWindow.onYouTubeIframeAPIReady;
				playerWindow.onYouTubeIframeAPIReady = () => {
					previousReady?.();
					initYoutube(code);
				};
				if (!playerDocument.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
					const script = playerDocument.createElement('script');
					script.src = 'https://www.youtube.com/iframe_api';
					playerDocument.head.appendChild(script);
				}
				return;
			}
			if (youtube?.Player) {
				const player = new youtube.Player(container.firstElementChild as HTMLElement, {
					videoId: code,
					playerVars: { rel: 0 },
					width: '100%',
					events: {
						onReady: () => {
							videoInitialized();
							playerRef.current = { playVideo: () => player.playVideo() };
							if (pendingPlayRef.current) {
								player.playVideo();
								pendingPlayRef.current = false;
							}
						},
						onStateChange: (event) => {
							if (event.data === -1) videoStarted();
							if (event.data === 1) youtubePlaying.current = true;
							if (event.data === 0) {
								if (youtubePlaying.current) videoEnded();
								youtubePlaying.current = false;
							}
						},
					},
				});
			}
		}, 0);
	};

	const initVimeo = () => {
		if (!file) return;
		const vimeoRegex = /vimeo.*\/(\d+)/i;
		const match = vimeoRegex.exec(file.FileUrl);
		if (!match || match.length < 2) return;
		const vimeoVideoId = match[1];

		setTimeout(() => {
			const container = vimeoContainerRef.current;
			const playerDocument = container?.ownerDocument;
			const playerWindow = playerDocument?.defaultView;
			if (!container || !playerDocument || !playerWindow) return;

			const loadPlayer = () => {
				const vimeo = playerWindow.Vimeo;
				if (!vimeo) return;
				const vPlayer = new vimeo.Player(container, {
					id: vimeoVideoId,
					responsive: true,
					autoplay: autoplay,
				});
				vPlayer.on('loaded', () => videoInitialized());
				vPlayer.on('play', (data: unknown) => {
					if (data) videoStarted();
				});
				vPlayer.on('ended', () => videoEnded());
				playerRef.current = { playVideo: () => vPlayer.play() };
			};

			if (playerWindow.Vimeo?.Player) loadPlayer();
			else if (!playerDocument.querySelector('script[src*="player.vimeo.com"]')) {
				const script = playerDocument.createElement('script');
				script.src = 'https://player.vimeo.com/api/player.js';
				script.onload = loadPlayer;
				playerDocument.body.appendChild(script);
			} else {
				playerDocument
					.querySelector('script[src*="player.vimeo.com"]')
					?.addEventListener('load', loadPlayer, { once: true });
			}
		}, 0);
	};

	const initBrightcove = () => {
		if (!file) return;
		const regex = /players\.brightcove\.net\/(.*)\/(.*)_default\/index\.html\?videoId=(.*)/i;
		const match = regex.exec(file.FileUrl);
		if (!match || match.length < 4) return;

		const accountId = match[1];
		const playerId = match[2];
		const bcVideoId = match[3];
		const elId = videoIdRef.current;

		setTimeout(() => {
			const container = brightcoveContainerRef.current;
			const playerDocument = container?.ownerDocument;
			const playerWindow = playerDocument?.defaultView;
			if (!container || !playerDocument || !playerWindow) return;

			container.innerHTML = `<video-js id="${elId}" data-video-id="${bcVideoId}" data-player="${playerId}" data-account="${accountId}" data-embed="default" class="video-js" controls></video-js>`;

			const scriptSrc = `https://players.brightcove.net/${accountId}/${playerId}_default/index.min.js`;

			const loadPlayer = () => {
				const brightcove = playerWindow.bc;
				if (brightcove) {
					const videoEl = brightcove(elId);
					videoInitialized();
					videoEl.on('play', videoStarted);
					videoEl.on('ended', videoEnded);
				}
			};

			if (!playerDocument.querySelector(`script[src="${scriptSrc}"]`)) {
				const script = playerDocument.createElement('script');
				script.src = scriptSrc;
				script.onload = loadPlayer;
				playerDocument.body.appendChild(script);
			} else {
				loadPlayer();
			}
		}, 0);
	};

	const initMediaflowPlayer = () => {
		setTimeout(() => {
			const playerWindow = mediaflowFrameRef.current?.ownerDocument.defaultView;
			if (!playerWindow) return;
			const messageHandler = (e: MessageEvent) => {
				if (e.origin !== 'https://play.mediaflow.com') return;
				if (e.source !== mediaflowFrameRef.current?.contentWindow) return;

				let d = e.data;
				if (typeof d === 'string') {
					try {
						d = JSON.parse(d);
					} catch {
						return;
					}
				}
				if (!d || typeof d !== 'object') return;

				const evt = d.event || d.type || d.name;
				if (evt === 'play' || evt === 'playing') videoStarted();
				if (evt === 'ended' || evt === 'finish' || evt === 'complete') videoEnded();
			};

			mediaflowCleanupRef.current?.();
			playerWindow.addEventListener('message', messageHandler);
			mediaflowCleanupRef.current = () => playerWindow.removeEventListener('message', messageHandler);
			videoInitialized();
		}, 0);
	};

	const getThumbnailUrl = () => {
		if (!file?.Thumbnail) return '';
		return getPathWithRootPath(file.Thumbnail);
	};

	const getFileUrl = () => {
		if (!file?.FileUrl) return '';
		return getPathWithRootPath(file.FileUrl);
	};

	if (!file && !fileId) return null;

	return (
		<div className='biz-mediabank-video-v2'>
			{showPoster && !initialized && file?.Thumbnail?.trim() && (
				<div
					className='biz-mediabank-video-poster'
					style={{ backgroundImage: `url(${getThumbnailUrl()})` }}
					onClick={handlePosterClick}
				>
					<div className='biz-mediabank-video-play-overlay' />
				</div>
			)}

			{initialized && (
				<div className='biz-mediabank-video-content'>
					{videoType === 'Youtube' && (
						<div className='biz-mediabank-video-youtube' ref={youtubeContainerRef}>
							<div id={videoIdRef.current} />
						</div>
					)}

					{videoType === 'Native' && (
						<video ref={videoElRef} controls onPlay={videoStarted} onEnded={videoEnded}>
							<source src={getFileUrl()} />
						</video>
					)}

					{videoType === 'Vimeo' && <div className='biz-mediabank-video-vimeo' ref={vimeoContainerRef} />}

					{videoType === 'Brightcove' && (
						<div className='biz-mediabank-video-brightcove' ref={brightcoveContainerRef} />
					)}

					{videoType === 'Mediaflow' && (
						<div className='biz-mediabank-video-mediaflow'>
							<iframe
								ref={mediaflowFrameRef}
								style={{ width: '100%' }}
								allow='autoplay; fullscreen'
								allowFullScreen
								src={file?.FileUrl}
							/>
						</div>
					)}

					{videoType === 'External' && (
						<div className='biz-mediabank-video-external'>
							<iframe style={{ width: '100%' }} src={file?.FileUrl} />
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default BizMediabankVideo;
